// AV-prep tool (/speech-loop): fetches one TTS render and loops it forever so
// VDMX audio-reactive lighting can be dialed in without re-rendering speech.
import React, {useRef, useState} from "react";

const DEFAULT_TEXT = "The world is forever changed. A crowd has gathered, not entirely sure what it signed up for. Let us meet them. Tonight, the Machine judges all.";
const SPEECH_PLAYBACK_RATE = 1.2;

const base64ToUint8 = (b64) => {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
};

export const SpeechLoop = () => {
    const [text, setText] = useState(DEFAULT_TEXT);
    const [status, setStatus] = useState('idle'); // idle | loading | looping
    const audioRef = useRef(null);

    const stop = () => {
        const a = audioRef.current;
        if (a) { a.pause(); try { URL.revokeObjectURL(a.src); } catch (e) {} }
        audioRef.current = null;
        setStatus('idle');
    };

    const start = async () => {
        stop();
        setStatus('loading');
        const res = await fetch("/text-to-speech-timestamps?text=" + encodeURIComponent(text));
        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        const chunks = [];
        let buffer = '';
        while (true) {
            const {value, done} = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, {stream: true});
            let idx;
            while ((idx = buffer.indexOf('\n')) !== -1) {
                const line = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 1);
                if (!line) continue;
                try {
                    const chunk = JSON.parse(line);
                    if (chunk.audio_base64) chunks.push(base64ToUint8(chunk.audio_base64));
                } catch (e) {}
            }
        }
        const blob = new Blob(chunks, {type: 'audio/mpeg'});
        const audio = new Audio(URL.createObjectURL(blob));
        audio.loop = true;
        audio.playbackRate = SPEECH_PLAYBACK_RATE;
        audio.addEventListener('playing', () => { audio.playbackRate = SPEECH_PLAYBACK_RATE; });
        audioRef.current = audio;
        await audio.play();
        setStatus('looping');
    };

    return <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'black',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
        padding: 40,
    }}>
        <div style={{fontSize: 13, fontWeight: 800, letterSpacing: 4, textTransform: 'uppercase', opacity: 0.5}}>
            Speech Loop — lighting calibration
        </div>
        <div style={{fontSize: 48, fontWeight: 'bold', textAlign: 'center', maxWidth: 1200, backgroundColor: 'black'}}>
            {text} {status === 'looping' && <span>■</span>}
        </div>
        <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            style={{width: 600, maxWidth: '90vw', background: '#111', color: 'white', border: '1px solid #444', borderRadius: 6, padding: 10, fontSize: 16, fontFamily: 'inherit'}}
        />
        <div style={{display: 'flex', gap: 16}}>
            <button
                onClick={status === 'looping' ? stop : start}
                disabled={status === 'loading'}
                style={{
                    padding: '16px 44px',
                    border: '3px solid #e8b15a',
                    borderRadius: 6,
                    background: 'linear-gradient(180deg, #2a2520 0%, #14110d 100%)',
                    color: '#f1d28b',
                    fontSize: 24,
                    fontWeight: 900,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                }}
            >
                {status === 'idle' ? '▶ Loop It' : status === 'loading' ? 'Rendering…' : '■ Stop'}
            </button>
        </div>
        <div style={{fontSize: 14, opacity: 0.5}}>One ElevenLabs render per Loop It press; loops from buffer after that.</div>
    </div>;
};
